import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Icon from '@/components/Icon';

export const icons = {
  folder: { icon: 'folder', name: 'Folder' },
  'circle-dollar': { icon: 'circle-dollar', name: 'Coin' },
  'book-blank': { icon: 'book-blank', name: 'Book' },
  'graduation-cap': { icon: 'graduation-cap', name: 'Graduation Cap' },
  pencil: { icon: 'pencil', name: 'Pencil' },
  'pen-nib': { icon: 'pen-nib', name: 'Pen' },
  'brackets-curly': { icon: 'brackets-curly', name: 'Brackets' },
  'square-terminal': { icon: 'square-terminal', name: 'Terminal' },
  music: { icon: 'music', name: 'Music' },
  popcorn: { icon: 'popcorn', name: 'Popcorn' },
  'paintbrush-pencil': { icon: 'paintbrush-pencil', name: 'Paintbrush' },
  palette: { icon: 'palette', name: 'Palette' },
  stethoscope: { icon: 'stethoscope', name: 'Stethoscope' },
  'star-of-life': { icon: 'star-of-life', name: 'Badge' },
  flower: { icon: 'flower', name: 'Flower' },
  briefcase: { icon: 'briefcase', name: 'Briefcase' },
  // NOTE: keys must match the backend `allowedCollectionIcons` allowlist —
  // the grid passes the KEY to onIconSelect, and it's saved verbatim.
  'chart-simple': { icon: 'chart-simple', name: 'Chart' },
  medal: { icon: 'medal', name: 'Medal' },
  dumbbell: { icon: 'dumbbell', name: 'Dumbbell' },
  notebook: { icon: 'notebook', name: 'Notebook' },
  'scale-balanced': { icon: 'scale-balanced', name: 'Scale Balanced' },
  'globe-stand': { icon: 'globe-stand', name: 'Globe Stand' },
  plane: { icon: 'plane', name: 'Plane' },
  globe: { icon: 'globe', name: 'Globe' },
  wrench: { icon: 'wrench', name: 'Wrench' },
  paw: { icon: 'paw', name: 'Paw' },
  flask: { icon: 'flask', name: 'Flask' },
  brain: { icon: 'brain', name: 'Brain' },
  heart: { icon: 'heart', name: 'Heart' },
  'tree-large': { icon: 'tree-large', name: 'Tree' },
};

const createStyles = (colors, isXS) => StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 2,
    padding: 2,
  },
  iconButton: {
    width: isXS ? 48 : 36,
    height: isXS ? 48 : 36,
    borderRadius: isXS ? 24 : 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  iconButtonHover: {
    backgroundColor: colors.solid_color_secondary,
  },
  iconButtonSelected: {
    backgroundColor: colors.solid_color_secondary,
  },
});

const IconButton = React.memo(({ iconKey, icon, isSelected, onPress, iconSize, colors, styles }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      style={({ hovered: webHovered }) => [
        styles.iconButton,
        (webHovered || hovered) && !isSelected && styles.iconButtonHover,
        isSelected && styles.iconButtonSelected,
      ]}
      onPress={() => onPress?.(iconKey)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
    >
      <Icon
        name={icon}
        size={iconSize}
        color={colors.text_color_primary}
      />
    </Pressable>
  );
});

function CollectionIconsGrid({ selectedIcon, onIconSelect }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const iconSize = isXS ? 24 : 18;

  return (
    <View style={styles.grid}>
      {Object.entries(icons).map(([key, { icon, name }]) => (
        <IconButton
          key={key}
          iconKey={key}
          icon={icon}
          isSelected={selectedIcon === key}
          onPress={onIconSelect}
          iconSize={iconSize}
          colors={colors}
          styles={styles}
        />
      ))}
    </View>
  );
}

export default React.memo(CollectionIconsGrid);
